import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import {
  addOrUpdateTransition,
  downloadIfspTransition,
  getIfsp,
  getIfspTransition,
} from '../../../services/ifspClient/IfspApi';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setIfspTransition } from '../../../redux/features/ifsp/ifspTransitionSlice';
import IfspTransitionPlan from './IfspTransitionPlan';
import IfspTransitionPrep from './IfspTransitionPrep';
import MediumTypography from '../../../components/formlib/MediumTypography';
import ButtonComponent from '../../../components/formlib/ButtonComponent';
import { AlertColor } from '@mui/material';
import { LoaderContext, LoaderContextType } from '../../../layouts/AppSidebar';
import SnackBarComponent from '../../../components/formlib/SnackBarComponent';
import ModalPopup from '../../../components/formlib/ModalPopup';
import { useIfspUnsavedChanges } from '../ifspUnsavedChanges';
import IfspHeld from '../ifspComponents/IfspHeld';
import { useIfspTransitionValidationCheckToSubmit } from './ifspTransitionValidationCheck';
import { ReactComponent as DownloadIcon } from '../../../assets/images/downloadIcon.svg';
import { ApiError, isCustomError } from '../../../services/ApiResponseHandler';
import dayjs from 'dayjs';
import {
  updateIfspClientDetails,
  updateOriginalIfspClientDetails,
} from '../../../redux/features/ifsp/ifspClientDetailsSlice';
import { getClientDocumentFileName } from '../../../utils/fileUtils';

const IfspTransitionMainScreen = () => {
  const dispatch = useAppDispatch();

  const ifspClientDetails = useAppSelector(
    (state) => state.ifspClientDetails.ifspClientDetails,
  );
  const ifspTransitionDetails = useAppSelector(
    (state) => state.ifspTransitionDetails.ifspTransitionData,
  );

  const ifspOriginalTransitionDetails = useAppSelector(
    (state) => state.ifspTransitionDetails.ifspOriginalTransitionData,
  );

  const ifspHeatBeatDetails = useAppSelector(
    (state) => state.ifspHeatBeatDetails,
  );

  const ifspPermission = useAppSelector((state) => state.ifspPermissionDetails);

  const [openUnSavedModal, setOpenUnSavedModal] = useState<boolean>(false);
  const [transitionRenderKey, setTransitionRenderKey] = useState(0);
  const [open, setOpen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false);
  const [toastrId, setToastrId] = useState('');
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const { isTransitionChanged } = useIfspUnsavedChanges();
  const parentRef = useRef<HTMLDivElement>(null);

  const validationsError = useIfspTransitionValidationCheckToSubmit(
    ifspTransitionDetails,
  );

  useEffect(() => {
    toggleLoader(true);
    getIfspTransition()
      .then((res) => {
        toggleLoader(false);
        dispatch(setIfspTransition(res));
      })
      .catch(() => {
        setOpen(true);
        setToastrVariable('error');
        setToastrId('IFSP.Transition.GetError');
        setToastrDefaultMessage('Failed to get IFSP Transition data');
      });
  }, [ifspHeatBeatDetails.transitionEmpId]);

  const getTbrFlagDetails = () => {
    const clientId = localStorage.getItem('ClientId');
    getIfsp(clientId as string)
      .then((data) => {
        const updatedDetails = {
          ...ifspClientDetails,
          tbrUpdateRequired: data.tbrUpdateRequired,
          outcomeTbrUpdateRequired: data.outcomeTbrUpdateRequired,
          serviceDeliveryTbrUpdateRequired:
            data.serviceDeliveryTbrUpdateRequired,
          transitionTbrUpdateRequired: data.transitionTbrUpdateRequired,
          isCommonAreaChecked: data.isCommonAreaChecked,
          isOutcomeChecked: data.isOutcomeChecked,
          isServiceDeliveryChecked: data.isServiceDeliveryChecked,
          isTransitionChecked: data.isTransitionChecked,
        };

        dispatch(updateIfspClientDetails(updatedDetails));
        dispatch(updateOriginalIfspClientDetails(updatedDetails));
      })
      .catch((error) => {
        toggleLoader(false);
        setOpen(true);
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrVariable('error');
          setToastrId('IFSP.ClientDetails.GetError');
          setToastrDefaultMessage('Failed to get IFSP data');
        }
      });
  };

  const handleAddOrUpdate = () => {
    if (Object.keys(validationsError).length > 0) {
      setIsSaveClicked(true);
      setOpen(true);
      setToastrVariable('error');
      setToastrId('IFSP.Transition.UpdateError');
      setToastrDefaultMessage(
        'Please correct the highlighted fields before submitting.',
      );
      setTimeout(() => {
        setOpen(false);
      }, 3000);
      return;
    } else {
      setIsSaveClicked(false);
    }
    toggleLoader(true);
    addOrUpdateTransition(ifspTransitionDetails)
      .then(() => {
        toggleLoader(false);
        setOpen(true);
        setToastrVariable('success');
        setToastrId('IFSP.Transition.SaveSuccess');
        setToastrDefaultMessage('IFSP Transition saved successfully');
        return getIfspTransition()
          .then((res) => {
            dispatch(setIfspTransition(res));
            return getTbrFlagDetails();
          })
          .catch(() => {
            setOpen(true);
            setToastrVariable('error');
            setToastrId('IFSP.Transition.GetError');
            setToastrDefaultMessage('Failed to get IFSP Transition data');
          });
      })
      .catch(() => {
        toggleLoader(false);
        setOpen(true);
        setToastrVariable('error');
        setToastrId('IFSP.Transition.SaveError');
        setToastrDefaultMessage('Failed to save IFSP Transition');
      });
  };

  const handleDownload = () => {
    toggleLoader(true);
    const clientId = localStorage.getItem('ClientId');
    const localTimezone = dayjs.tz.guess();
    if (clientId) {
      downloadIfspTransition(clientId, localTimezone)
        .then((response) => {
          const blob = new Blob([response], { type: 'application/pdf' });

          // Create a Blob URL
          const blobUrl = URL.createObjectURL(blob);

          // Open the Blob URL in a new tab
          const anchor = document.createElement('a');
          anchor.href = blobUrl;
          anchor.target = '_blank';
          anchor.download = getClientDocumentFileName(
            `Individualized Family Service Plan (IFSP).pdf`,
          ); // Set desired file name here

          // Append anchor to document body
          document.body.appendChild(anchor);

          // Trigger anchor click
          anchor.click();

          window.open(blobUrl, '_blank');

          // Clean up after use
          URL.revokeObjectURL(blobUrl);
          document.body.removeChild(anchor);

          toggleLoader(false);
        })
        .catch((error) => {
          toggleLoader(false);
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('ConsentForms.downloadErrorMsg');
            setToastrDefaultMessage('Failed to download');
          }
        });
    }
  };

  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <Box sx={{ backgroundColor: '#E4EEEE' }}>
      {ifspTransitionDetails.isCurrentlyHeld ? (
        <Box sx={{ position: 'relative' }}>
          <IfspHeld ifspHealBy={ifspTransitionDetails?.heldBy} />
        </Box>
      ) : null}

      <Box
        ref={parentRef}
        style={{
          pointerEvents: ifspTransitionDetails.isCurrentlyHeld
            ? 'none'
            : 'auto',
          opacity: ifspTransitionDetails.isCurrentlyHeld ? '0.5' : '1 ',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onKeyDownCapture={(e) => {
          if (ifspTransitionDetails.isCurrentlyHeld) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onFocus={() => {
          if (ifspTransitionDetails.isCurrentlyHeld) {
            if (parentRef.current) parentRef.current.focus();
          }
        }}
      >
        <SnackBarComponent
          open={open}
          handleClose={handleCloseSnakBar}
          successOrError={toastrVariable}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
        />
        <ModalPopup
          open={openUnSavedModal}
          description="formUnsavedChangesMessage"
          onCancel={() => {
            setOpenUnSavedModal(false);
          }}
          onOk={() => {
            dispatch(setIfspTransition(ifspOriginalTransitionDetails));
            setTransitionRenderKey((prev) => prev + 1);
            setTimeout(() => {
              setOpenUnSavedModal(false);
            }, 500);
          }}
          labelId1="Clientpage.cancelbtn"
          negativeActionLabel="Cancel"
          labelId2="Clientpage.Okbtn"
          positiveActionLabel="Ok"
        />
        <Box className="flex__ justifyContentSpaceBtw alignItemCenter pr-xlg">
          <MediumTypography
            labelid="IFSP.Transition"
            defaultlabel="Transition"
            fontSize="18px"
            fontweight={600}
            className="pb-md pl-md pt-md"
          />
          <DownloadIcon className="cursorPointer" onClick={handleDownload} />
        </Box>
        {ifspTransitionDetails.ifspId && (
          <Box key={transitionRenderKey}>
            <IfspTransitionPrep isSaveClicked={isSaveClicked} />
            <IfspTransitionPlan isSaveClicked={isSaveClicked} />
          </Box>
        )}
        <Box
          component="div"
          className="flex__ justifyContent-FlexEnd pt-lg pb-lg pr-md"
        >
          <ButtonComponent
            className="btn-primary btn-cancel"
            labelId={'cancelText'}
            defaultLabelId="Cancel"
            variantType="outlined"
            onClick={() => {
              if (isTransitionChanged) {
                setOpenUnSavedModal(true);
              } else {
                setOpenUnSavedModal(false);
              }
            }}
            disabled={!ifspPermission.hasClientEditPermission}
          />
          <ButtonComponent
            className="btn-primary btn-submit  ml-md"
            labelId="IFSP.SaveTransition"
            defaultLabelId="Save Transition"
            variantType="contained"
            onClick={() => {
              handleAddOrUpdate();
            }}
            disabled={!ifspPermission.hasClientEditPermission}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default IfspTransitionMainScreen;
